import { useState } from 'react';
import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { KanbanColumn } from './KanbanColumn';
import { ProspectCard } from './ProspectCard';
import { EditProspectDialog } from '@/components/prospects/EditProspectDialog';
import { DeleteConfirmDialog } from '@/components/prospects/DeleteConfirmDialog';
import { PIPELINE_STAGES } from '@/lib/constants';
import { useProspects, type Prospect, type PipelineStage } from '@/hooks/useProspects';
import type { Id } from '../../../convex/_generated/dataModel.js';
import { toast } from 'sonner';

interface KanbanBoardProps {
  onEditProspect?: (prospect: Prospect) => void;
  onDeleteProspect?: (prospectId: string) => void;
}

function CollapsedDropZone({ 
  isCollapsed, 
  count, 
  onToggle 
}: { 
  isCollapsed: boolean; 
  count: number; 
  onToggle: () => void; 
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: 'closed_lost',
  });

  return (
    <div
      ref={setNodeRef}
      className={`
        flex items-center justify-between p-4 rounded-lg border-2 border-dashed transition-colors
        ${isOver 
          ? 'border-red-400 bg-red-50 dark:bg-red-950/20' 
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
        }
      `}
    >
      <Button
        variant="ghost"
        onClick={onToggle}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        Closed Lost ({count})
      </Button>
      {isOver && (
        <span className="text-xs text-red-600 dark:text-red-400 font-medium">
          Drop to close prospect
        </span>
      )}
    </div>
  );
}

export function KanbanBoard({ onEditProspect, onDeleteProspect }: KanbanBoardProps) {
  const { prospects, getProspectsByStage, moveProspectToStage } = useProspects();
  const [activeProspect, setActiveProspect] = useState<Prospect | null>(null);
  const [editingProspect, setEditingProspect] = useState<Prospect | null>(null);
  const [deletingProspect, setDeletingProspect] = useState<Prospect | null>(null);
  const [isClosedLostCollapsed, setIsClosedLostCollapsed] = useState(true);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Handle edit and delete actions
  const handleEditProspect = (prospect: Prospect) => {
    setEditingProspect(prospect);
    onEditProspect?.(prospect);
  };

  const handleDeleteProspect = (prospectId: string) => {
    const prospect = prospects.find((p: Prospect) => p._id === prospectId);
    if (prospect) {
      setDeletingProspect(prospect);
    }
    onDeleteProspect?.(prospectId);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const prospect = prospects.find((p: Prospect) => p._id === active.id);
    setActiveProspect(prospect || null);
  };

  const handleDragOver = () => {
    // This could be used for visual feedback during drag
    // Currently not needed for our implementation
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveProspect(null);

    if (!over) return;

    const prospectId = active.id as string;
    const newStage = over.id as PipelineStage;
    
    // Find the prospect being moved
    const prospect = prospects.find((p: Prospect) => p._id === prospectId);
    if (!prospect) return;

    // If dropped in the same stage, no action needed
    if (prospect.stage === newStage) return;

    // Validate that the new stage is valid
    const validStages = PIPELINE_STAGES.map(s => s.value);
    if (!validStages.includes(newStage)) {
      toast.error('Invalid pipeline stage');
      return;
    }

    try {
      // Update the prospect's stage
      await moveProspectToStage(prospectId as Id<"prospects">, newStage);
      
      toast.success(
        `Moved ${prospect.company} to ${PIPELINE_STAGES.find(s => s.value === newStage)?.label}`
      );
    } catch (error) {
      toast.error('Failed to move prospect. Please try again.');
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6 h-full">
        {/* Main Pipeline - Active Flow */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 flex-1">
          {PIPELINE_STAGES.filter(stage => stage.value !== 'closed_lost').map((stage) => {
            const stageProspects = getProspectsByStage(stage.value);
            
            return (
              <KanbanColumn
                key={stage.value}
                stage={stage.value}
                title={stage.label}
                prospects={stageProspects}
                onEditProspect={handleEditProspect}
                onDeleteProspect={handleDeleteProspect}
              />
            );
          })}
        </div>
        
        {/* Closed Lost - Collapsible Section with Drop Zone */}
        <div className="border-t pt-4">
          {isClosedLostCollapsed ? (
            <CollapsedDropZone
              isCollapsed={isClosedLostCollapsed}
              count={getProspectsByStage('closed_lost').length}
              onToggle={() => setIsClosedLostCollapsed(!isClosedLostCollapsed)}
            />
          ) : (
            <div className="space-y-4">
              <CollapsedDropZone
                isCollapsed={isClosedLostCollapsed}
                count={getProspectsByStage('closed_lost').length}
                onToggle={() => setIsClosedLostCollapsed(!isClosedLostCollapsed)}
              />
              <div className="grid grid-cols-1 max-w-sm">
                {PIPELINE_STAGES.filter(stage => stage.value === 'closed_lost').map((stage) => {
                  const stageProspects = getProspectsByStage(stage.value);
                  
                  return (
                    <KanbanColumn
                      key={stage.value}
                      stage={stage.value}
                      title={stage.label}
                      prospects={stageProspects}
                      onEditProspect={handleEditProspect}
                      onDeleteProspect={handleDeleteProspect}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Drag overlay for visual feedback */}
      <DragOverlay>
        {activeProspect ? (
          <div className="rotate-3 scale-105">
            <ProspectCard 
              prospect={activeProspect}
            />
          </div>
        ) : null}
      </DragOverlay>

      {/* Edit and Delete Dialogs */}
      {editingProspect && (
        <EditProspectDialog
          prospect={editingProspect}
          trigger={<div />}
        />
      )}

      {deletingProspect && (
        <DeleteConfirmDialog
          prospect={deletingProspect}
          trigger={<div />}
        />
      )}
    </DndContext>
  );
}